import {Component, OnInit, inject, DestroyRef} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DataService} from '../../services/store/data.service';
import {Authority} from '../../classes/enums/Authority';
import {StoreSummary} from '../../classes/models/store/StoreSummary';
import {PredictionRotaWeek} from '../../classes/models/PredictionRotaWeek';
import {map} from 'rxjs/operators';
import {DailyPredictionSortService} from '../../services/prediction/daily-prediction-sort.service';
import {
  DailyPredictionRotaGroupByWeekService
} from '../../services/prediction/daily-prediction-rota-group-by-week.service';
import {ActivatedRoute} from '@angular/router';
import {DailyFootfallPrediction} from '../../classes/models/prediction/DailyFootfallPrediction';
import {RotaSummary} from '../../classes/models/rota/RotaSummary';
import {ToastrService} from 'ngx-toastr';
import {ChangeStoreService} from '../../services/store/change-store.service';
import {FootfallPredictionService} from '../../services/http/prediction/footfall-prediction.service';
import {RotaService} from '../../services/http/rota/rota-service';
import moment from 'moment';
import {DateParsePipe} from '../../pipes/date-parse.pipe';
import {DateFormatPipe} from '../../pipes/date-format.pipe';
import {RemoveWhiteSpacePipe} from '../../pipes/remove-white-space.pipe';
import {StoreNavigationComponent} from '../../shared/components/store-navigation/store-navigation.component';
import {SHARED_IMPORTS} from "../../shared/standalone-imports";

/**
 * This component displays the main page, which displays either a list of all stores, or list of footfall predictions and rotas
 * for the selected store.
 */
@Component({
  selector: 'app-landing',
  templateUrl: './landing.component.html',
  styleUrls: ['./landing.component.less'],
  imports: [SHARED_IMPORTS]
})
export class LandingComponent implements OnInit {
  private route = inject(ActivatedRoute);
  dataService = inject(DataService);
  private dailyPredictionSortService = inject(DailyPredictionSortService);
  private dailyPredictionRotaGroupByWeekService = inject(DailyPredictionRotaGroupByWeekService);
  private changeStoreService = inject(ChangeStoreService);
  private footfallPredictionsService = inject(FootfallPredictionService);
  private rotaService = inject(RotaService);
  private destroyRef = inject(DestroyRef);
  private destroy$ = new Subject<void>();

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.destroy$.next();
      this.destroy$.complete();
    });
  }


  /** The selected store, for which prediction and rota weeks are displayed. It can be `null`, if no store was selected. */
  selectedStore: StoreSummary;
  /** A list of daily footfall predictions for the selected store. */
  dailyPredictions: DailyFootfallPrediction[] = [];
  /** Rota summaries for the selected store. */
  rotaSummaries: RotaSummary[] = [];
  /** A list of weeks' information to be displayed. */
  predictionRotaWeeks: PredictionRotaWeek[] = [];

  /** `True` if request to footfall predictions is in progress. */
  predictionsLoading = false;
  /** `True` if request to rota summaries is in progress. */
  rotaSummariesLoading = false;

  /**
   * Retrieves predictions and rotas from backend, then combines them into a list of {@link PredictionRotaWeek}.
   */
  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.selectedStore = this.dataService.selectedStore;
        const from = this.dataService.rangeFrom;
        const to = this.dataService.rangeTo;
        this.predictionRotaWeeks = [];
        if (this.selectedStore && this.hasAccessToViewPredictions()) {
          this.getPredictions(this.dataService.selectedStore.slug, from, to);
        }
        if (this.selectedStore && this.hasAccessToViewRotas()) {
          this.getRotaSummaries(this.dataService.selectedStore.slug, from, to);
        }
        this.dataService.predictionRotaWeeks = this.predictionRotaWeeks;
      }
    });
  }

  private getPredictions(store: string, from: string, to: string): void {
    this.predictionsLoading = true;
    this.footfallPredictionsService.getPredictions(store, from, to)
            .pipe(
                    map(dailyPredictions => (this.dailyPredictionSortService.sortByDate(dailyPredictions))),
                    takeUntil(this.destroy$)
            )
            .subscribe({
              next: dailyPredictions => this.dailyPredictions = dailyPredictions,
              complete: () => {
                this.predictionRotaWeeks = this.dailyPredictionRotaGroupByWeekService.groupPredictions(
                        this.predictionRotaWeeks,
                        this.dailyPredictions
                );
                this.predictionsLoading = false;
              }
            });
  }

  private getRotaSummaries(store: string, from: string, to: string): void {
    this.rotaSummariesLoading = true;
    const fromWeek = moment(from).startOf('week').isoWeekday(1).format('YYYY-MM-DD');
    const toWeek = moment(to).startOf('week').isoWeekday(1).format('YYYY-MM-DD');
    this.rotaService.getRotaSummaries(store, fromWeek, toWeek)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: rotaSummaries => this.rotaSummaries = rotaSummaries,
              complete: () => {
                this.predictionRotaWeeks = this.dailyPredictionRotaGroupByWeekService.groupRotas(
                        this.predictionRotaWeeks,
                        this.rotaSummaries
                );
                this.rotaSummariesLoading = false;
              }
            });
  }

  /**
   * Checks if request to footfall predictions or rota summaries are still in progress.
   */
  requestsAreLoading(): boolean {
    return this.predictionsLoading || this.rotaSummariesLoading;
  }

  /**
   * Checks if a user has access to view footfall predictions.
   */
  hasAccessToViewPredictions(): boolean {
    return this.dataService.userHasAuthority(Authority.AUTH_SEE_PREDICTIONS);
  }

  /**
   * Checks if a user has access to view rotas.
   */
  hasAccessToViewRotas(): boolean {
    return this.dataService.userHasAuthority(Authority.AUTH_SEE_ROTAS);
  }

  /**
   * Updates the selected store.
   * @param store the new selected store.
   */
  navigateToStore(store: StoreSummary): void {
    this.changeStoreService.selectStore(store);
  }
}
